import { CheckCircle2, CircleAlert, CircleX } from "lucide-react";
import * as React from "react";

import type { FragmentStatus, FragmentSummary } from "@/api/affdo";
import { formatRmsdKcal } from "@/api/affdo";
import { EmptyState } from "@/components/layout/EmptyState";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

/**
 * Per-fragment AFFDO parameter-quality table.
 *
 * One row per ``TorsionRefinementResult`` artifact produced by the
 * run. Sorted worst-RMSD-first by default so the user's eye lands
 * on the fragments that need attention. Click a row to navigate to
 * its small-multiples view (rendered by ``DihedralRMSDGrid`` —
 * lands in the second sub-PR).
 *
 * The table is the ``ui/table`` primitive over a real ``<table>`` (not
 * ag-grid) — at pilot scale
 * the typical Flow run produces tens of fragments, not thousands,
 * and a clean tabular-nums layout reads better in the AFFDO tab
 * than a windowed grid. We pick up ag-grid for runs/artifacts where
 * the row count actually warrants it.
 */
export interface FragmentSummaryTableProps {
  fragments: FragmentSummary[] | undefined;
  isLoading?: boolean;
  /** Currently-selected fragment_id (highlighted row). */
  selectedFragmentId?: string | null;
  /** Click handler for a row — used to switch the small-multiples
   * panel to the selected fragment. */
  onSelect?: (fragmentId: string) => void;
  /**
   * Drop the two RMSD columns (flowstack#991).
   *
   * The workspace's side panel is 26rem, and five numeric columns do not fit
   * — they compress into each other, which is precisely the overlapping-label
   * failure the operator reported on the old 3-up torsion cards. Compact, the
   * table is a SELECTOR (fragment · status · fit/failed) and the selected
   * row's RMSDs are spelled out beneath it, so no number is lost — it moves
   * to where there is room to read it. The Analysis tab, which has a full
   * page, keeps all five.
   */
  compact?: boolean;
  className?: string;
}

const STATUS_BADGE: Record<
  FragmentStatus,
  { label: string; cls: string; Icon: typeof CheckCircle2 }
> = {
  ok: {
    label: "ok",
    cls: "border-state-completed-border bg-state-completed-bg text-state-completed-fg",
    Icon: CheckCircle2,
  },
  partial: {
    label: "partial",
    cls: "border-state-failed-validation-border bg-state-failed-validation-bg text-state-failed-validation-fg",
    Icon: CircleAlert,
  },
  failed: {
    label: "failed",
    cls: "border-state-failed-scientific-border bg-state-failed-scientific-bg text-state-failed-scientific-fg",
    Icon: CircleX,
  },
};

function formatRmsd(value: number | null): string {
  if (value === null) return "—";
  return formatRmsdKcal(value);
}

export function FragmentSummaryTable({
  fragments,
  isLoading = false,
  selectedFragmentId = null,
  onSelect,
  compact = false,
  className,
}: FragmentSummaryTableProps): JSX.Element {
  // Worst-RMSD-first ordering so problematic fragments surface to the
  // top of the list. Null RMSD (all-failed) sinks to the bottom — the
  // failed-status badge already calls it out, and the user is more
  // likely to want to see the high-RMSD-but-finished fits first.
  const sorted = React.useMemo(() => {
    if (!fragments) return [];
    return [...fragments].sort((a, b) => {
      const av = a.max_fit_rmsd_kcal_per_mol;
      const bv = b.max_fit_rmsd_kcal_per_mol;
      if (av === null && bv === null) return a.fragment_id.localeCompare(b.fragment_id);
      if (av === null) return 1;
      if (bv === null) return -1;
      return bv - av;
    });
  }, [fragments]);

  if (isLoading) {
    return <Skeleton className={cn("h-64 w-full", className)} />;
  }
  if (!fragments || fragments.length === 0) {
    return (
      <EmptyState
        title="No AFFDO fragments to report."
        description="This run did not produce any TorsionRefinementResult artifacts. The AFFDO quality view surfaces once a torsion-fit Tile has completed."
        className={className}
      />
    );
  }

  return (
    <Card className={cn("overflow-hidden", className)}>
      <Table className="tabular-nums">
        <TableHeader surface="tinted" className="text-[10px]">
          <TableRow>
            <TableHead>Fragment</TableHead>
            <TableHead>Status</TableHead>
            <TableHead align="right">Fit / Failed</TableHead>
            {!compact && (
              <>
                <TableHead align="right">Max RMSD</TableHead>
                <TableHead align="right">Mean RMSD</TableHead>
              </>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((row) => {
            const badge = STATUS_BADGE[row.status];
            const isSelected = selectedFragmentId === row.fragment_id;
            return (
              // `interactive` stays at its "none" default: this row carries a
              // SELECTED state as well as a hover one, and two states is
              // richer than the primitive's single axis. The exception lives
              // at the call site through `cn()`, where it stays visible.
              <TableRow
                key={row.fragment_id}
                className={cn(
                  "cursor-pointer border-border-subtle/40 transition-colors",
                  isSelected ? "bg-surface-2" : "hover:bg-surface-2/60"
                )}
                onClick={() => onSelect?.(row.fragment_id)}
                aria-selected={isSelected}
              >
                <TableCell className="font-mono text-xs">{row.fragment_id}</TableCell>
                <TableCell>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider",
                      badge.cls
                    )}
                  >
                    <badge.Icon className="h-3 w-3" aria-hidden />
                    {badge.label}
                  </span>
                </TableCell>
                <TableCell align="right">
                  <span className="text-foreground">{row.n_fitted}</span>
                  <span className="text-muted-foreground"> / </span>
                  <span
                    className={cn(
                      row.n_failed > 0 ? "text-state-failed-scientific-fg" : "text-muted-foreground"
                    )}
                  >
                    {row.n_failed}
                  </span>
                </TableCell>
                {!compact && (
                  <>
                    <TableCell align="right" className="font-mono text-xs">
                      {formatRmsd(row.max_fit_rmsd_kcal_per_mol)}
                      <span className="ml-1 text-[10px] text-muted-foreground">kcal/mol</span>
                    </TableCell>
                    <TableCell align="right" className="font-mono text-xs">
                      {formatRmsd(row.mean_fit_rmsd_kcal_per_mol)}
                      <span className="ml-1 text-[10px] text-muted-foreground">kcal/mol</span>
                    </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}
